import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { FirestoreCollection } from 'react-firestore';
import FireflySubscription from './misc/FireflySubscription';
import Loading from './misc/Loading';

import Dashnav from './Dashboard/dashnav';
import Landing from './layout/landing';
import SignUp from './account/hookSignUp';
import SignIn from './account/hookSignIn';
import GoogleSignIn from './account/googleSignIn';
import PasswordForgetPage from './account/PasswordForget';
import StartupPricing from './Pricing/startupPricing';
import Lists from './Lists/HeavenListIndex';
import List from './Lists/infiniteList';
import ListNew from './Lists/listNew';
import ListContact from './Lists/listContact';
import JournalistDashboard from './Dashboard/JournalistDashboard';
import HomeDashboard from './Dashboard/HeavenHomeDashboard.js';
import Account from './account/Account';
import AdminPanel from './Admin';
import JournalistDatabaseHome from './layout/journalist-database';
import AgencyPricing from './Pricing/agencyPricing';
import Journalists from './Journalists';
import JournalistForm from './Journalists/journalistForm';
import JournalistList from './Journalists/journalistList';
import Profile from './Journalists/profile';
import Error from './misc/Error';

/**
 * NavRoute wrapper for protected routes with navigation
 * Handles auth checking and provides subscription data to child components
 */
const NavRouteWrapper = ({ Component, authCheck = false }) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  return (
    <div>
      <Dashnav path={location.pathname} />
      <FireflySubscription>
        {({
          isLoading, error, subscription, auth, status, plan,
        }) => {
          if (isLoading) {
            return <Loading />;
          }

          if (error) {
            return <Error error={error} />;
          }

          if (!auth) {
            if (authCheck) {
              return <Navigate to="/signin" replace />;
            }
            return <Component />;
          }

          if (auth) {
            return (
              <FirestoreCollection
                path="lists"
                sort="createdOn:desc"
                filter={['createdBy', '==', auth.uid]}
              >
                {({ error, isLoading, data }) => {
                  if (error) {
                    return <Error error={error} />;
                  }
                  if (isLoading) {
                    return <Loading />;
                  }
                  return (
                    <Component
                      subscription={subscription}
                      status={status}
                      plan={plan}
                      lists={data}
                      auth={auth}
                      params={params}
                      navigate={navigate}
                    />
                  );
                }}
              </FirestoreCollection>
            );
          }
          return null;
        }}
      </FireflySubscription>
    </div>
  );
};

/**
 * Application Routes
 * React Router v6 syntax
 */
const AppRoutes = () => (
  <Routes>
    {/* Public landing page routes */}
    <Route path="/" element={<Landing />} />
    <Route path="/agency-pricing" element={<AgencyPricing />} />
    <Route path="/pricing" element={<StartupPricing />} />
    <Route path="/journalist-database" element={<JournalistDatabaseHome />} />

    {/* Auth routes */}
    <Route path="/signup" element={<SignUp />} />
    <Route path="/signin" element={<SignIn />} />
    <Route path="/signin/google" element={<GoogleSignIn />} />
    <Route path="/passwordforget" element={<PasswordForgetPage />} />

    {/* Protected dashboard routes with nav */}
    <Route path="/account" element={<NavRouteWrapper Component={Account} authCheck={true} />} />
    <Route path="/admin" element={<NavRouteWrapper Component={AdminPanel} authCheck={true} />} />
    <Route path="/dashboard" element={<NavRouteWrapper Component={HomeDashboard} authCheck={true} />} />
    <Route path="/journalist-dashboard" element={<NavRouteWrapper Component={JournalistDashboard} authCheck={true} />} />
    <Route path="/journalist-dashboard/bulk" element={<NavRouteWrapper Component={JournalistDashboard} authCheck={true} />} />

    {/* Lists routes */}
    <Route path="/lists" element={<NavRouteWrapper Component={Lists} authCheck={true} />} />
    <Route path="/lists/new" element={<NavRouteWrapper Component={ListNew} authCheck={true} />} />
    <Route path="/list/:slug" element={<NavRouteWrapper Component={List} authCheck={true} />} />
    <Route path="/list/:slug/:objectID" element={<NavRouteWrapper Component={ListContact} authCheck={true} />} />

    {/* Public journalist profile routes */}
    <Route path="/journalists" element={<Journalists />} />
    <Route path="/journalists/new" element={<JournalistForm />} />
    <Route path="/journalists/list" element={<JournalistList />} />
    <Route path="/journalists/:slug" element={<Profile />} />

    {/* 404 fallback */}
    <Route path="*" element={<Error />} />
  </Routes>
);

export default AppRoutes;
